import React, { useCallback, useEffect, useState } from 'react';
import { Check, X } from 'lucide-react';
import { supabase } from '../supabaseClient';
import { t, formatDateTime } from '../i18n';
import { usageDetail, resolveRemovalRequest } from '../lib/masterData';
import { usageLine } from '../lib/masterDataUsage';
import { Button, Badge, Alert, Spinner, EmptyState } from './ui';

/**
 * Admin's side of the removal requests professionals file (migration 0066).
 *
 * A pending request says, before anyone answers it, whether anything uses the
 * entry and so what approving does: delete it, or deactivate it. A resolved one
 * says what was asked, by whom, who answered, when, and what happened, from what
 * the request itself stored, because the entry may be gone.
 */

const personName = (person) => (person ? `${person.first_name} ${person.last_name}` : '—');

const nameOf = (request) => request.entity_label ?? t('masterData.nameNotKept');

/** What approving will do, from the counts taken when the list loaded. */
const UsagePreview = ({ usage }) => {
  if (usage === undefined) return <p className="text-sm text-slate-500">{t('masterData.usageCounting')}</p>;
  if (usage === null) return <p className="text-sm text-amber-800">{t('masterData.usageFailed')}</p>;
  if (!usage.exists) return <p className="text-sm text-slate-600">{t('masterData.usageGone')}</p>;
  if (usage.total === 0) {
    return (
      <p className="text-sm font-medium text-emerald-800">
        {usage.subcategories > 0
          ? t('masterData.usageNoneSubcategories', { count: usage.subcategories })
          : t('masterData.usageNone')}
      </p>
    );
  }
  return (
    <p className="text-sm font-medium text-amber-900">
      {t('masterData.usageSome', { uses: usageLine(usage.usage) })}
    </p>
  );
};

/** What happened to a resolved request's entry. */
const outcomeText = (request) => {
  if (request.status !== 'approved') return '';
  if (request.outcome === 'deleted') return t('masterData.outcomeDeleted');
  if (request.outcome === 'deactivated') {
    const uses = usageLine(request.usage_counts);
    return uses ? t('masterData.outcomeDeactivated', { uses }) : t('masterData.outcomeDeactivatedPlain');
  }
  return t('masterData.outcomeGone');
};

const RemovalRequests = ({ onResolved }) => {
  const [requests, setRequests] = useState(null);
  // What uses each pending request's entry, by request id: missing while it is
  // counted, null when the count failed.
  const [usages, setUsages] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [notice, setNotice] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    const { data, error: loadError } = await supabase
      .from('master_data_removal_requests')
      .select(`*,
        requester:therapists!master_data_removal_requests_requested_by_therapist_id_fkey(first_name, last_name),
        reviewer:therapists!master_data_removal_requests_reviewed_by_therapist_id_fkey(first_name, last_name)`)
      .order('created_at', { ascending: false });
    if (loadError) { setError(loadError.message); setRequests([]); return; }
    setRequests(data ?? []);

    const pending = (data ?? []).filter((request) => request.status === 'pending');
    const counted = await Promise.all(pending.map(async (request) => {
      try {
        return [request.id, await usageDetail(request.entity_type, request.entity_id)];
      } catch {
        return [request.id, null];
      }
    }));
    setUsages(Object.fromEntries(counted));
  }, []);

  useEffect(() => { load(); }, [load]);

  const resolve = async (request, approve) => {
    setBusyId(request.id);
    setError(null);
    setNotice(null);
    const { data, error: resolveError } = await resolveRemovalRequest({ requestId: request.id, approve });
    setBusyId(null);
    if (resolveError) { setError(resolveError.message); return; }

    const name = data?.label ?? nameOf(request);
    if (!approve) setNotice(t('masterData.rejectedNotice', { name }));
    else if (data?.outcome === 'deleted') setNotice(t('masterData.deletedNotice', { name }));
    else if (data?.outcome === 'deactivated') {
      setNotice(t('masterData.deactivatedNotice', { name, uses: usageLine(data.usage) }));
    } else setNotice(t('masterData.goneNotice', { name }));

    await load();
    onResolved?.();
  };

  if (!requests) return <Spinner />;

  const pending = requests.filter((request) => request.status === 'pending');
  const resolved = requests.filter((request) => request.status !== 'pending');

  const requestedLine = (request) => `${t(`masterData.entityType.${request.entity_type}`)} · ${t('masterData.requestedBy', {
    name: personName(request.requester),
    when: formatDateTime(request.created_at),
  })}`;

  return (
    <div className="space-y-6">
      {error && <Alert tone="danger">{error}</Alert>}
      {notice && <Alert tone="success">{notice}</Alert>}

      {pending.length === 0 ? (
        <EmptyState icon={Check} title={t('masterData.noPendingRequests')} />
      ) : (
        <ul className="space-y-3">
          {pending.map((request) => (
            <li key={request.id} className="rounded-lg border-2 border-amber-200 bg-amber-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 space-y-1">
                  <p className="font-medium text-slate-900">{nameOf(request)}</p>
                  <p className="text-xs text-slate-600">{requestedLine(request)}</p>
                  {request.reason && <p className="text-sm text-slate-700">“{request.reason}”</p>}
                  <UsagePreview usage={usages[request.id]} />
                </div>
                <div className="flex gap-2">
                  {/* Not before the count is in: what approving does depends on it. */}
                  <Button
                    variant="success"
                    loading={busyId === request.id}
                    disabled={busyId !== null || usages[request.id] === undefined}
                    onClick={() => resolve(request, true)}
                  >
                    <Check size={16} />{t('masterData.approve')}
                  </Button>
                  <Button variant="secondary" disabled={busyId !== null} onClick={() => resolve(request, false)}>
                    <X size={16} />{t('masterData.reject')}
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}

      {resolved.length > 0 && (
        <div>
          <h3 className="mb-2 text-sm font-semibold uppercase tracking-wide text-slate-500">
            {t('masterData.resolvedRequests')}
          </h3>
          <ul className="divide-y divide-slate-100 rounded-lg border border-slate-200">
            {resolved.map((request) => (
              <li key={request.id} className="space-y-0.5 px-3 py-2 text-sm">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className={`font-medium ${request.entity_label ? 'text-slate-800' : 'text-slate-500'}`}>
                    {nameOf(request)}
                  </span>
                  <Badge tone={request.status === 'approved' ? 'success' : 'neutral'}>
                    {t(`masterData.status.${request.status}`)}
                  </Badge>
                </div>
                <p className="text-xs text-slate-500">
                  {requestedLine(request)}{request.reason ? ` · “${request.reason}”` : ''}
                </p>
                <p className="text-xs text-slate-600">
                  {t('masterData.resolvedBy', {
                    name: personName(request.reviewer),
                    when: formatDateTime(request.reviewed_at),
                  })} {outcomeText(request)}
                </p>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default RemovalRequests;
